import type { Object3D } from '@wonderlandengine/api';
import { mat3, quat, vec3 } from 'gl-matrix';

const EPSILON = 1e-6;

function reachAtBend(upper: number, lower: number, degrees: number): number {
    return Math.sqrt(Math.max(EPSILON * EPSILON,
        upper ** 2 + lower ** 2 + 2 * upper * lower * Math.cos(degrees * Math.PI / 180)));
}

/** Allocation-free analytic triangle solve. Pole is a direction, not a position. */
export class TwoBoneIK {
    readonly elbow = vec3.create();
    readonly wrist = vec3.create();
    readonly normal = vec3.create();
    private readonly direction = vec3.fromValues(0, 0, -1);
    private readonly bend = vec3.fromValues(0, -1, 0);
    private readonly projectedPole = vec3.create();
    private readonly fallback = vec3.create();

    solve(
        shoulder: vec3, target: vec3, pole: vec3, upperLength: number, lowerLength: number,
        minBendDegrees = 5, maxBendDegrees = 150,
    ): boolean {
        if (!(upperLength > EPSILON && lowerLength > EPSILON)
            || !Number.isFinite(upperLength + lowerLength + minBendDegrees + maxBendDegrees)) return false;
        for (let i = 0; i < 3; ++i) {
            if (!Number.isFinite(shoulder[i]) || !Number.isFinite(target[i]) || !Number.isFinite(pole[i])) return false;
        }

        vec3.sub(this.fallback, target, shoulder);
        const distance = vec3.length(this.fallback);
        if (distance > EPSILON) vec3.scale(this.direction, this.fallback, 1 / distance);

        // Keep away from straight/folded singularities. Bend = 0 means straight.
        const minBend = Math.max(0.1, Math.min(175, minBendDegrees));
        const maxBend = Math.max(minBend, Math.min(179, maxBendDegrees));
        const reach = Math.max(reachAtBend(upperLength, lowerLength, maxBend),
            Math.min(reachAtBend(upperLength, lowerLength, minBend), distance));

        vec3.scaleAndAdd(this.projectedPole, pole, this.direction, -vec3.dot(pole, this.direction));
        if (vec3.squaredLength(this.projectedPole) < 1e-4) {
            // Pole nearly parallel to reach: reuse the last bend plane.
            vec3.scaleAndAdd(this.projectedPole, this.bend, this.direction, -vec3.dot(this.bend, this.direction));
        }
        if (vec3.squaredLength(this.projectedPole) < EPSILON) {
            vec3.set(this.fallback, Math.abs(this.direction[0]) < 0.8 ? 1 : 0,
                Math.abs(this.direction[0]) < 0.8 ? 0 : 1, 0);
            vec3.scaleAndAdd(this.projectedPole, this.fallback, this.direction,
                -vec3.dot(this.fallback, this.direction));
        }
        vec3.normalize(this.bend, this.projectedPole);
        const along = (upperLength ** 2 - lowerLength ** 2 + reach ** 2) / (2 * reach);
        const height = Math.sqrt(Math.max(0, upperLength ** 2 - along ** 2));
        vec3.scaleAndAdd(this.elbow, shoulder, this.direction, along);
        vec3.scaleAndAdd(this.elbow, this.elbow, this.bend, height);
        vec3.scaleAndAdd(this.wrist, shoulder, this.direction, reach);
        vec3.cross(this.normal, this.bend, this.direction);
        vec3.normalize(this.normal, this.normal);
        return true;
    }
}

/**
 * Rotates a contiguous upper-arm -> forearm -> wrist chain. Local translations
 * and scales are never solved/extended; the hand position emerges from the chain.
 * Uniform authored scale is supported (lengths measured after restoring rest).
 */
export class ArmIK {
    private readonly upperRest: Float32Array;
    private readonly lowerRest: Float32Array;
    private readonly handRest: Float32Array;
    private readonly triangle = new TwoBoneIK();
    private readonly shoulder = vec3.create();
    private readonly elbow = vec3.create();
    private readonly wrist = vec3.create();
    private readonly upperDirection = vec3.create();
    private readonly lowerDirection = vec3.create();
    private readonly restNormal = vec3.create();
    private readonly pole = vec3.create();
    private readonly desiredDirection = vec3.create();
    private readonly frameX = vec3.create();
    private readonly frameY = vec3.create();
    private readonly frameZ = vec3.create();
    private readonly frameMatrix = mat3.create();
    private readonly sourceFrame = quat.create();
    private readonly targetFrame = quat.create();
    private readonly rotation = quat.create();
    private readonly upperRotation = quat.create();
    private readonly lowerRotation = quat.create();
    private readonly twistAxis = vec3.create();
    private readonly currentHandRotation = quat.create();
    private readonly wristDelta = quat.create();
    private readonly twistRotation = quat.create();

    static create(root: Object3D, hand: Object3D | null, side: 'Left' | 'Right'): ArmIK | null {
        if (!hand) return null;
        const upper = root.findByNameRecursive(`Avatar_${side}Arm`)[0];
        const lower = root.findByNameRecursive(`Avatar_${side}ForeArm`)[0];
        if (!upper || !lower || lower.parent !== upper || hand.parent !== lower) return null;
        const shoulder = upper.getPositionWorld(vec3.create());
        const elbow = lower.getPositionWorld(vec3.create());
        const wrist = hand.getPositionWorld(vec3.create());
        if (vec3.distance(shoulder, elbow) < EPSILON || vec3.distance(elbow, wrist) < EPSILON) return null;
        return new ArmIK(upper, lower, hand, side === 'Left' ? -1 : 1);
    }

    private constructor(
        readonly upper: Object3D, readonly lower: Object3D, readonly hand: Object3D,
        private readonly side: number,
    ) {
        this.upperRest = upper.getTransformLocal(new Float32Array(8));
        this.lowerRest = lower.getTransformLocal(new Float32Array(8));
        this.handRest = hand.getTransformLocal(new Float32Array(8));
    }

    restore(): void {
        this.upper.setTransformLocal(this.upperRest);
        this.lower.setTransformLocal(this.lowerRest);
        this.hand.setTransformLocal(this.handRest);
    }

    /** On-demand debug snapshot; never restores or changes the visible pose. */
    getTrackingDiagnostics(target: vec3, bodyRotation: quat) {
        const shoulder = this.upper.getPositionWorld(vec3.create());
        const elbow = this.lower.getPositionWorld(vec3.create());
        const wrist = this.hand.getPositionWorld(vec3.create());
        const upperLength = vec3.distance(shoulder, elbow);
        const lowerLength = vec3.distance(elbow, wrist);
        const targetDistance = vec3.distance(shoulder, target);
        const inverseBody = quat.invert(quat.create(), bodyRotation);
        const targetOffset = vec3.sub(vec3.create(), target, shoulder);
        const wristOffset = vec3.sub(vec3.create(), wrist, shoulder);
        vec3.transformQuat(targetOffset, targetOffset, inverseBody);
        vec3.transformQuat(wristOffset, wristOffset, inverseBody);
        return {
            shoulderWorld: Array.from(shoulder),
            targetWorld: Array.from(target),
            wristWorld: Array.from(wrist),
            targetFromShoulderBodyAxes: Array.from(targetOffset),
            wristFromShoulderBodyAxes: Array.from(wristOffset),
            upperLength,
            lowerLength,
            targetDistance,
            wristDistance: vec3.distance(shoulder, wrist),
            targetError: vec3.distance(target, wrist),
            targetBeyondFullReach: targetDistance > upperLength + lowerLength,
        };
    }

    /**
     * Share wrist roll with the forearm after positional IK. Rotate about the
     * actual elbow-wrist line, so the wrist endpoint and segment length stay fixed.
     * Restored hand axes account for the imported joint orientation on each side.
     */
    alignWristRoll(desiredHandRotation: quat, weight: number): void {
        if (!Number.isFinite(weight) || weight <= 0) return;
        this.lower.getPositionWorld(this.elbow);
        this.hand.getPositionWorld(this.wrist);
        vec3.sub(this.twistAxis, this.wrist, this.elbow);
        if (vec3.squaredLength(this.twistAxis) < EPSILON * EPSILON) return;
        vec3.normalize(this.twistAxis, this.twistAxis);
        this.hand.getRotationWorld(this.currentHandRotation);

        // Swing/twist decomposition of the world-space wrist rotation difference.
        // Project onto the actual forearm axis, avoiding hardcoded palm axes/signs.
        quat.invert(this.wristDelta, this.currentHandRotation);
        quat.multiply(this.wristDelta, desiredHandRotation, this.wristDelta);
        if (this.wristDelta[3] < 0) quat.scale(this.wristDelta, this.wristDelta, -1);
        const projection = this.wristDelta[0] * this.twistAxis[0]
            + this.wristDelta[1] * this.twistAxis[1] + this.wristDelta[2] * this.twistAxis[2];
        // A pure 180-degree swing has no uniquely defined axial twist.
        if (projection ** 2 + this.wristDelta[3] ** 2 < EPSILON * EPSILON) return;
        const angle = 2 * Math.atan2(projection, this.wristDelta[3]);
        quat.setAxisAngle(this.twistRotation, this.twistAxis, angle * Math.min(1, weight));
        this.lower.getRotationWorld(this.lowerRotation);
        quat.multiply(this.lowerRotation, this.twistRotation, this.lowerRotation);
        this.lower.setRotationWorld(this.lowerRotation);
    }

    solve(target: vec3, bodyRotation: quat, minBend: number, maxBend: number): boolean {
        // Reset first: no accumulated rotations or offsets from a previous frame.
        this.restore();
        this.upper.getPositionWorld(this.shoulder);
        this.lower.getPositionWorld(this.elbow);
        this.hand.getPositionWorld(this.wrist);
        this.upper.getRotationWorld(this.upperRotation);
        this.lower.getRotationWorld(this.lowerRotation);
        vec3.sub(this.upperDirection, this.elbow, this.shoulder);
        vec3.sub(this.lowerDirection, this.wrist, this.elbow);
        const upperLength = vec3.length(this.upperDirection);
        const lowerLength = vec3.length(this.lowerDirection);
        vec3.normalize(this.upperDirection, this.upperDirection);
        vec3.normalize(this.lowerDirection, this.lowerDirection);

        // Elbows prefer outward, downward and slightly backward, relative to body.
        vec3.set(this.pole, this.side * 0.65, -1, 0.35);
        vec3.transformQuat(this.pole, this.pole, bodyRotation);
        if (!this.triangle.solve(this.shoulder, target, this.pole, upperLength, lowerLength, minBend, maxBend)) return false;

        vec3.cross(this.restNormal, this.upperDirection, this.lowerDirection);
        if (vec3.squaredLength(this.restNormal) < 1e-4) {
            vec3.cross(this.restNormal, this.pole, this.upperDirection);
        }
        if (vec3.squaredLength(this.restNormal) < EPSILON) {
            vec3.set(this.restNormal, 0, 0, 1);
            vec3.transformQuat(this.restNormal, this.restNormal, bodyRotation);
        }
        vec3.normalize(this.restNormal, this.restNormal);

        vec3.sub(this.desiredDirection, this.triangle.elbow, this.shoulder);
        this.aim(this.upper, this.upperDirection, this.desiredDirection, this.upperRotation);
        // Read the resulting elbow, rather than teleporting it to triangle.elbow.
        this.lower.getPositionWorld(this.elbow);
        vec3.sub(this.desiredDirection, this.triangle.wrist, this.elbow);
        this.aim(this.lower, this.lowerDirection, this.desiredDirection, this.lowerRotation);
        return true;
    }

    private frame(out: quat, direction: vec3, normal: vec3): void {
        vec3.normalize(this.frameX, direction);
        vec3.cross(this.frameY, normal, this.frameX);
        if (vec3.squaredLength(this.frameY) < EPSILON) {
            // Defensive fallback for degenerate authored rest axes.
            vec3.set(this.frameZ, Math.abs(this.frameX[0]) < 0.8 ? 1 : 0,
                Math.abs(this.frameX[0]) < 0.8 ? 0 : 1, 0);
            vec3.cross(this.frameY, this.frameZ, this.frameX);
        }
        vec3.normalize(this.frameY, this.frameY);
        vec3.cross(this.frameZ, this.frameX, this.frameY);
        for (let i = 0; i < 3; ++i) {
            this.frameMatrix[i] = this.frameX[i];
            this.frameMatrix[3 + i] = this.frameY[i];
            this.frameMatrix[6 + i] = this.frameZ[i];
        }
        quat.fromMat3(out, this.frameMatrix);
        quat.normalize(out, out);
    }

    private aim(bone: Object3D, restDirection: vec3, direction: vec3, restRotation: quat): void {
        // Mapping two frames fixes segment direction AND roll about that direction.
        this.frame(this.sourceFrame, restDirection, this.restNormal);
        this.frame(this.targetFrame, direction, this.triangle.normal);
        quat.invert(this.rotation, this.sourceFrame);
        quat.multiply(this.rotation, this.targetFrame, this.rotation);
        quat.multiply(this.rotation, this.rotation, restRotation);
        bone.setRotationWorld(this.rotation);
    }
}